import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import Api from 'api';
import FixedLoader from 'components/FixedLoader';
import FixedError from 'components/FixedError';
import TalentNavbar from 'components/TalentNavbar';
import TalentPageLayout from 'components/TalentPageLayout';
import TalentBackButton from 'components/TalentBackButton/TalentBackButton';
import { CalendarMonthOutlined, BusinessOutlined, SendOutlined, AccessTime } from '@mui/icons-material';
import dayjs from 'dayjs';
import { getTalentPaymentViewState } from 'utils/talentPaymentState';
import InvoiceModal from '../InvoicePage/InvoiceModal';
import {
  Content,
  Empty,
  Header,
  ItemAmount,
  ItemHeader,
  ItemMeta,
  ItemTitle,
  List,
  ListItem,
  PageContainer,
  RightColumn,
  SortBar,
  SortButton,
  StatusBadge,
  Subtitle,
  Title,
} from './InvoicesList.styles';

type SortBy = 'date' | 'office';

// T2-6049: Talent Invoices list now mirrors the DO invoices design
// (T2-6048) — same card layout with status pill + amount stacked on the
// right and paper-plane / clock icons in the meta row.
const InvoicesList: React.FC = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jobs, setJobs] = useState<any[]>([]);
  const [sortBy, setSortBy] = useState<SortBy>('date');

  // Invoice modal state
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [selectedJobData, setSelectedJobData] = useState<any>(null);
  const [isViewInvoice, setIsViewInvoice] = useState(false);
  const [loadingJobDetails, setLoadingJobDetails] = useState(false);

  // Get current user info for navbar
  const currentUser = (window as any)?.TempStars?.User?.getCurrentUser?.();
  const hygienist = currentUser?.hygienist;
  const firstName = hygienist?.firstName || '';
  const lastName = hygienist?.lastName || '';
  const photoUrl = hygienist?.photoUrl || undefined;
  const isHygOrAsst = hygienist?.type === (window as any).TempStars?.HYGIENIST_TYPES?.HYGIENIST ||
                      hygienist?.type === (window as any).TempStars?.HYGIENIST_TYPES?.ASSISTANT;

  const handleBack = useCallback(() => {
    history.push({ pathname: 'hygienistHome' });
  }, [history]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await Api.getInvoices();
      const list = Array.isArray(resp) ? resp : [];
      setJobs(list.map((j: any) => ({
        ...j,
        hourlyRate: j?.invoice?.hourlyRate ?? j?.invoice?.hourRate, // legacy compat
      })));
    } catch (e: any) {
      console.error('getInvoices error', e);
      setError('Failed to load invoices.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() => {
    const base = [...jobs];
    if (sortBy === 'date') {
      return base.sort((a, b) => {
        const ad = a?.invoice?.sentOn ? dayjs(a.invoice.sentOn).valueOf() : 0;
        const bd = b?.invoice?.sentOn ? dayjs(b.invoice.sentOn).valueOf() : 0;
        return bd - ad;
      });
    }
    return base.sort((a, b) => {
      const an = (a?.location?.practiceName || '').localeCompare(b?.location?.practiceName || '');
      return an;
    });
  }, [jobs, sortBy]);

  const openJob = useCallback(async (job: any) => {
    const id = Number(job?.id ?? job?.jobId);
    if (!id || isNaN(id)) {
      // Fallback: attempt legacy bridge only if no id found
      (window as any).TempStars?.Hygienist?.gotoInvoicePage?.(job);
      return;
    }

    // Fetch full job details for the modal (same approach as Invoice.tsx page)
    setLoadingJobDetails(true);
    setError(null);
    try {
      const [fetchedJob, invoice] = await Promise.all([
        Api.getJobDetails(id),
        Api.getInvoiceForJob(id).catch(() => null)
      ]);

      // Merge hygienist data from current user when not present
      const jobWithHygienist = {
        ...fetchedJob,
        hygienist: {
          firstName: hygienist?.firstName || '',
          lastName: hygienist?.lastName || '',
          preferredName: hygienist?.preferredName || null,
          address: hygienist?.address || '',
          etransferEmail: hygienist?.etransferEmail || '',
          country: hygienist?.country || 0,
          ...(fetchedJob.hygienist || {}),
        },
      };

      const invoiceObj = invoice && typeof invoice === 'object' ? invoice : null;
      const hasValidInvoice = !!(invoiceObj && (invoiceObj.id || (invoiceObj as any)?.invoice?.id));

      const transformedJob = {
        ...jobWithHygienist,
        invoice: hasValidInvoice ? invoiceObj : job.invoice,
      };

      setSelectedJobData(transformedJob);
      setIsViewInvoice(!!job?.invoice || hasValidInvoice);
      setIsInvoiceModalOpen(true);
    } catch (e: any) {
      console.error('Error fetching job details:', e);
      setError('Failed to load invoice details.');
    } finally {
      setLoadingJobDetails(false);
    }
  }, [hygienist]);

  const handlePaidStatusChange = useCallback((invoiceId: number, isPaid: boolean) => {
    setJobs((prev) => prev.map((job) => {
      if (job?.invoice?.id !== invoiceId) return job;
      return {
        ...job,
        invoice: {
          ...job.invoice,
          hygienistMarkedPaid: isPaid ? 1 : 0,
        },
      };
    }));
  }, []);

  const handleInvoiceModalClose = useCallback(() => {
    setIsInvoiceModalOpen(false);
    setSelectedJobData(null);
  }, []);

  const handleInvoiceSuccess = useCallback(() => {
    load();
    handleInvoiceModalClose();
  }, [load, handleInvoiceModalClose]);

  return (
    <>
      <TalentPageLayout
        navbar={(
          <TalentNavbar
            firstName={firstName}
            lastName={lastName}
            photoUrl={photoUrl}
            isHygOrAsst={isHygOrAsst}
            instabookActivated={hygienist?.InstabookActivated === 1}
            isTestAccount={(hygienist as any)?.isTest === 1}
            showAddShiftButton={false}
            showJobBoardButton={false}
          />
        )}
      >
      <PageContainer>
        <TalentBackButton onBack={handleBack} />
        <Content>
          <Header>
            <Title>Invoices</Title>
            <Subtitle>Manage and review all your invoices</Subtitle>
          </Header>

          <SortBar>
            <SortButton active={sortBy === 'date'} onClick={() => setSortBy('date')}>
              <CalendarMonthOutlined fontSize="small" />
              Sort by Date
            </SortButton>
            <SortButton active={sortBy === 'office'} onClick={() => setSortBy('office')}>
              <BusinessOutlined fontSize="small" />
              Sort by Office
            </SortButton>
          </SortBar>

          {!loading && sorted.length === 0 && (
            <Empty>
              <div className="icon">📄</div>
              <div className="title">No Invoices Yet</div>
              <div className="subtitle">Invoices will appear here once shifts are completed</div>
            </Empty>
          )}

          <List>
            {sorted.map((job: any) => {
              const officeName = job?.location?.practiceName || 'Unknown Office';
              const hasInvoice = !!job?.invoice;
              const paymentView = getTalentPaymentViewState(job?.invoice);
              const sentDateRaw = job?.invoice?.sentOn;
              const sentDate = sentDateRaw ? dayjs(sentDateRaw).format('MMM DD, YYYY') : '—';
              const shiftDateRaw = job?.startDate || job?.shifts?.[0]?.shiftDate;
              const shiftDate = shiftDateRaw ? dayjs(shiftDateRaw).format('MMM DD, YYYY') : null;
              const amount = job?.invoice?.totalInvoiceAmt;
              // T2-7494: PAID, ACTION REQUIRED or UNPAID (no PROCESSING, no office states).
              const status: 'paid' | 'action_required' | 'sent' | 'draft' = !hasInvoice
                ? 'draft'
                : paymentView.isPaid
                  ? 'paid'
                  : paymentView.requiresAction
                    ? 'action_required'
                    : 'sent';
              const statusLabel = status === 'draft' ? 'NOT SENT' : paymentView.label;

              return (
                <ListItem key={job.id} onClick={() => openJob(job)}>
                  <ItemHeader>
                    <ItemTitle>{officeName}</ItemTitle>
                    <ItemMeta>
                      <span>
                        <SendOutlined fontSize="small" />
                        Sent: {sentDate}
                      </span>
                      {shiftDate && (
                        <span>
                          <AccessTime fontSize="small" />
                          Shift: {shiftDate}
                        </span>
                      )}
                    </ItemMeta>
                  </ItemHeader>
                  <RightColumn>
                    <StatusBadge
                      data-testid={`invoice-payment-${status}`}
                      paid={status === 'paid'}
                      sent={status === 'sent' || status === 'action_required'}
                    >
                      {statusLabel}
                    </StatusBadge>
                    {typeof amount === 'number' && amount > 0 && (
                      <ItemAmount>${amount.toFixed(2)}</ItemAmount>
                    )}
                  </RightColumn>
                </ListItem>
              );
            })}
          </List>
        </Content>
      </PageContainer>
      </TalentPageLayout>

      <FixedLoader loading={loading || loadingJobDetails} />
      <FixedError error={error as any} />

      {isInvoiceModalOpen && selectedJobData && (
        <InvoiceModal
          job={selectedJobData}
          isViewInvoice={isViewInvoice}
          isOpen={isInvoiceModalOpen}
          onClose={handleInvoiceModalClose}
          onSuccess={handleInvoiceSuccess}
          onPaidStatusChange={handlePaidStatusChange}
        />
      )}
    </>
  );
};

export default InvoicesList;
